import { Route, Navigate } from 'react-router-dom';
import Dashboard from '@/pages/Dashboard';
import Analytics from '@/pages/Analytics';
import Incidents from '@/pages/Incidents';
import CreateIncident from '@/pages/CreateIncident';
import IncidentDetails from '@/pages/IncidentDetails';
import ExecutiveProtectionLayout from '@/layouts/ExecutiveProtectionLayout';
import ExecutiveDetail from '@/pages/ExecutiveDetail';
import CreateProtectedPerson from '@/pages/CreateProtectedPerson';
import ActivityDetail from '@/pages/ActivityDetail';
import Activities from '@/pages/Activities';
import CreateActivity from '@/pages/CreateActivity';
import ProtectedPersons from '@/pages/ProtectedPersons';
import Geofences from '@/pages/Geofences';
import TripPlanner from '@/pages/TripPlanner';
import TravelRiskSettings from '@/pages/TravelRiskSettings';
import EPDashboard from '@/pages/EPDashboard';
import PushNotificationSettings from '@/pages/PushNotificationSettings';
import ThreatIntelligence from '@/pages/ThreatIntelligence';
import ThreatDetails from '@/pages/ThreatDetails';
import ThreatFeedItemDetails from '@/pages/ThreatFeedItemDetails';
import CreateThreatAssessment from '@/pages/CreateThreatAssessment';
import ReportPreviewPage from '@/pages/ReportPreviewPage';
import ManageFeeds from '@/pages/ManageFeeds';
import Investigations from '@/pages/Investigations';
import InvestigationDetails from '@/pages/InvestigationDetails';
import CreateInvestigation from '@/pages/CreateInvestigation';
import Subjects from '@/pages/Subjects';
import SubjectDetails from '@/pages/SubjectDetails';
import CreateSubject from '@/pages/CreateSubject';
import SecurityVendorDirectory from '@/pages/SecurityVendorDirectory';
import SecurityVendorCoverageMatrix from '@/pages/SecurityVendorCoverageMatrix';
import SecurityVendorDetail from '@/pages/SecurityVendorDetail';
import { SecurityModuleRoute } from '@/components/auth';
import SecurityPlaceholderPage from './pages/PlaceholderPage';

/**
 * Corporate Security module route definitions (VIS-197).
 * All routes are relative and rendered inside a <Route path="security"> parent
 * in App.tsx, so every Corporate Security URL follows the /security/<item> convention.
 */
export const securityRoutes = (
  <>
    {/* In-module Dashboard & Analytics (VIS-150). */}
    <Route
      index
      element={
        <SecurityModuleRoute>
          <Dashboard />
        </SecurityModuleRoute>
      }
    />
    <Route
      path="dashboard"
      element={
        <SecurityModuleRoute>
          <Dashboard />
        </SecurityModuleRoute>
      }
    />
    <Route
      path="analytics"
      element={
        <SecurityModuleRoute requiredPermission="analytics:read">
          <Analytics />
        </SecurityModuleRoute>
      }
    />
    <Route
      path="analytics/reports"
      element={
        <SecurityModuleRoute requiredPermission="analytics:read">
          <SecurityPlaceholderPage title="Security Reports" />
        </SecurityModuleRoute>
      }
    />

    {/* Incident Management */}
    <Route path="incidents" element={<Incidents />} />
    <Route path="incidents/create" element={<CreateIncident />} />
    <Route path="incidents/:id" element={<IncidentDetails />} />

    {/* Executive Protection - Specific routes first (outside tabbed layout) */}
    <Route path="executive-protection/protected-persons/create" element={<CreateProtectedPerson />} />
    <Route path="executive-protection/activities/create" element={<CreateActivity />} />
    <Route path="executive-protection/activities/:id" element={<ActivityDetail />} />
    <Route path="executive-protection/:id" element={<ExecutiveDetail />} />
    {/* Executive Protection - Tabbed routes */}
    <Route path="executive-protection" element={<ExecutiveProtectionLayout />}>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<EPDashboard />} />
      <Route path="protected-persons" element={<ProtectedPersons />} />
      <Route path="activities" element={<Activities />} />
      <Route path="geofences" element={<Geofences />} />
      <Route path="trip-planner" element={<TripPlanner />} />
      {/* Live Security Updates / push preferences (VIS-188). Registered as a
          CHILD of the layout so the EP tab bar renders on this path (L-047). */}
      <Route path="push-settings" element={<PushNotificationSettings />} />
      <Route path="travel-risk-settings" element={<TravelRiskSettings />} />
    </Route>

    {/* Threat Intelligence */}
    <Route path="threats" element={<ThreatIntelligence />} />
    <Route path="threats/create" element={<CreateThreatAssessment />} />
    <Route path="threats/feeds/manage" element={<ManageFeeds />} />
    <Route path="threats/feeds/:id" element={<ThreatFeedItemDetails />} />
    <Route path="threats/reports" element={<Navigate to="/security/threats" replace />} />
    <Route path="threats/reports/:reportId" element={<ReportPreviewPage />} />
    <Route path="threats/:id" element={<ThreatDetails />} />

    {/* Investigations */}
    <Route path="investigations" element={<Investigations />} />
    <Route path="investigations/create" element={<CreateInvestigation />} />
    <Route path="investigations/:id" element={<InvestigationDetails />} />

    {/* Subject Registry */}
    <Route path="subjects" element={<Subjects />} />
    <Route path="subjects/create" element={<CreateSubject />} />
    <Route path="subjects/:id" element={<SubjectDetails />} />

    {/* Security Vendor Directory (VIS-199) */}
    <Route
      path="vendor-directory"
      element={
        <SecurityModuleRoute requiredPermission="security_vendors:read">
          <SecurityVendorDirectory />
        </SecurityModuleRoute>
      }
    />
    {/* Coverage matrix (VIS-202) — before `:id` so it isn't captured as a vendor id. */}
    <Route
      path="vendor-directory/coverage-matrix"
      element={
        <SecurityModuleRoute requiredPermission="security_vendors:read">
          <SecurityVendorCoverageMatrix />
        </SecurityModuleRoute>
      }
    />
    <Route
      path="vendor-directory/:id"
      element={
        <SecurityModuleRoute requiredPermission="security_vendors:read">
          <SecurityVendorDetail />
        </SecurityModuleRoute>
      }
    />
  </>
);
